import React from 'react';

import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { ApolloProvider, useReactiveVar } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { ErrorBoundary } from '@sentry/react';

import { client } from 'core/graphql/client';
import { getInitialLanguage, languageVar } from 'core/state/languageState';
import { initTheme } from 'core/state/themeState';
import { buildSentryConfig } from 'core/utils/sentryConfig';
import { installChartTheme } from 'molecules/Charts/chartTheme';
import { installReactSelectTheme } from 'services/reactSelectTheme';

import { ConfigProvider } from 'antd';
import locale from 'antd/es/locale/ru_RU';
import localePL from 'antd/es/locale/pl_PL';

import moment from 'moment';

import './styles/theme.css';
import './styles/palette.css';
import './styles/antd-dark.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './index.css';

import './i18n';

import 'moment/locale/ru';
import 'moment/locale/pl';

moment.locale(getInitialLanguage());
installChartTheme();
installReactSelectTheme();
initTheme();

/**
 * Renders AntD's `ConfigProvider` against the reactive language var so the
 * locale follows runtime language switches. `key` remounts the subtree on
 * change so already-rendered moment dates pick up the new locale.
 */
const LocaleProvider: React.FC = ({ children }) => {
  const language = useReactiveVar(languageVar);
  return (
    <ConfigProvider
      key={language}
      locale={language === 'pl' ? localePL : locale}
    >
      {children}
    </ConfigProvider>
  );
};

// 100% transaction tracing instruments every session — dev-only. Production
// samples 10% (spec 0008 round 2).
const tracesSampleRate = process.env.NODE_ENV === 'production' ? 0.1 : 1.0;

// Сборка вшивает только NODE_ENV и REACT_APP_* (config/env.js), поэтому все
// переменные Sentry — с префиксом REACT_APP_. Перечислены поимённо: целиком
// `process.env` DefinePlugin вставил бы в бандл всем объектом.
// DSN — `REACT_APP_SENTRY_DSN`, иначе прежний выбор по теме; отправка только
// из боевой сборки и не с localhost (см. core/utils/sentryConfig).
Sentry.init({
  ...buildSentryConfig(
    {
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_THEME: process.env.REACT_APP_THEME,
      REACT_APP_SENTRY_DSN: process.env.REACT_APP_SENTRY_DSN,
      REACT_APP_SENTRY_ENVIRONMENT: process.env.REACT_APP_SENTRY_ENVIRONMENT,
      REACT_APP_SENTRY_RELEASE: process.env.REACT_APP_SENTRY_RELEASE,
    },
    window.location.hostname,
  ),
  integrations: [new Integrations.BrowserTracing()],
  tracesSampleRate,
});

ReactDOM.render(
  <React.StrictMode>
    <ApolloProvider client={client}>
      <ErrorBoundary>
        <BrowserRouter>
          <LocaleProvider>
            <App />
          </LocaleProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </ApolloProvider>
  </React.StrictMode>,
  document.getElementById('root'),
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
